import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { EligibleSchool } from 'Src/models/school';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { TOURNAMENT_SCHOOL_LOGO_FALLBACK } from 'Src/consts/tournament';
import { getTournamentNotificationAthleteSchools } from 'Src/helpers/service/adminSU/tournamentNotificationTable';
import { hasUnionParticipant } from 'Src/helpers/tournament/athleteSchools';

interface Props {
  user: AppUser;
  tournament: Tournament;
  onNotifySchools: (schools: EligibleSchool[]) => void;
  onCountChanged: (count: number) => void;
}

export function TournamentAthleteSchoolsEntries({ user, tournament, onNotifySchools, onCountChanged }: Props) {
  const [schools, setSchools] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [searchDraft, setSearchDraft] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const generation = React.useRef(0);
  const enabled = hasUnionParticipant(tournament);
  const storageKey = `tournamentAthleteSchoolNotificationRecipients:${user.userId}:${user.activeSchoolId}:${tournament.id}`;
  const clearSelection = () => {setSelected([]); window.sessionStorage.removeItem(storageKey);};
  const load = async () => {
    const current = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const rows = await getTournamentNotificationAthleteSchools(user, tournament.id);
      if (current !== generation.current) return;
      setSchools(rows);
      onCountChanged(rows.length);
      setSelected(previous => previous.filter(id => rows.some(row => String(row.id) === id)));
      setPage(1);
    } catch (e) {
      if (current === generation.current) setError('We could not load athletes’ schools. Please try again.');
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };
  React.useEffect(() => {
    setSchools([]);
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || '[]');
      setSelected(Array.isArray(saved) ? saved.map(row => String(row.id)) : []);
    } catch (e) {setSelected([]);}
    setSearch('');
    setSearchDraft('');
    setPage(1);
    if (enabled) load();
    return () => {
      generation.current++;
    };
  }, [tournament.id, enabled, user.activeSchoolId, user.roleSessionKey]);
  if (!enabled)
    return (
      <div className="utmEntries__content">
        <div className="alert alert-info">
          Athletes’ schools are available when a union participates in this tournament.
        </div>
      </div>
    );
  const filtered = schools.filter(school => school.name.toLowerCase().includes(search.toLowerCase()));
  const visible = filtered.slice((page - 1) * 20, page * 20);
  const selectedRows = schools.filter(school => selected.includes(String(school.id)));
  const selectRows = (rows: any[]) =>
    setSelected(previous => Array.from(new Set([...previous, ...rows.map(row => String(row.id))])));
  const toggle = (id: string) =>
    setSelected(previous => (previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]));
  return (
    <div className="utmEntries__content">
      <article className="utmEntries__panel utmParticipants unionTournamentsModern">
        <header>
          <div className="utmParticipants__heading">
            <h2>
              Athletes’ schools <b>{schools.length}</b>
            </h2>
            <div className="gridModernSearch">
              <form
                onSubmit={event => {
                  event.preventDefault();
                  setSearch(searchDraft.trim());
                  setPage(1);
                }}
              >
                <input
                  placeholder="Search athletes’ schools"
                  aria-label="Search athletes’ schools"
                  value={searchDraft}
                  onChange={event => setSearchDraft(event.target.value)}
                />
              </form>
            </div>
          </div>
          <button type="button" className="utmParticipants__manage" disabled={loading} onClick={load}>
            Refresh
          </button>
        </header>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {selected.length > 0 && (
          <div className="utmEligible__selection">
            <strong>Selected {selected.length}</strong>
            {user.activeRole === 'ADMIN' && (
              <button type="button" disabled={loading || Boolean(error)} onClick={() => onNotifySchools(selectedRows)}>
                Send notifications
              </button>
            )}
            <button type="button" className="is-clearSelection" onClick={clearSelection}>
              × Clear selection
            </button>
          </div>
        )}
        <div className="utmParticipants__tableWrap">
          <table aria-label="Athletes’ schools">
            <thead>
              <tr>
                <th>
                  <SelectAllCheckbox
                    compact
                    onSelectAllClick={() => selectRows(filtered)}
                    onSelectAllOnPageClick={() => selectRows(visible)}
                  onUnselectAllClick={clearSelection}
                    isChecked={filtered.length > 0 && filtered.every(row => selected.includes(String(row.id)))}
                    totalCount={filtered.length}
                    currentPage={page}
                    selectedItems={selectedRows}
                    pageItemsCount={visible.length}
                    isSelectDisabled={loading || Boolean(error)}
                  />
                </th>
                <th>School</th>
                <th>Athletes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="utmEligible__empty">
                    Loading athletes’ schools…
                  </td>
                </tr>
              ) : (
                visible.map(school => (
                  <tr key={school.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={school.name}
                        checked={selected.includes(String(school.id))}
                        onChange={() => toggle(String(school.id))}
                      />
                    </td>
                    <td>
                      <div className="utmParticipants__school">
                        <span className="utmParticipants__schoolLogo">
                          <img src={school.pic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                        </span>
                        <strong>{school.name}</strong>
                      </div>
                    </td>
                    <td>
                      <strong>{school.athleteCount}</strong>
                    </td>
                  </tr>
                ))
              )}
              {!loading && !visible.length && (
                <tr>
                  <td colSpan={3} className="utmEligible__empty">
                    <div className="utmTableEmptyState">
                      <strong>{search ? 'No schools match your search' : 'No athletes’ schools yet'}</strong>
                      <p>
                        {search
                          ? 'Try a different school name.'
                          : 'Schools will appear when children are added to the tournament squads.'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination2 modern currentPage={page} total={filtered.length} limit={20} setCurrentPageParams={setPage} />
      </article>
    </div>
  );
}
